import React, { useMemo, useRef } from 'react';
import { Map as MapView, MapMarker, MarkerContent, MarkerPopup, MapControls, useMap, type MapRef } from '../ui/map';
import './work-task-map.scss';

export interface MapWorkTask {
  WorkTaskOid: string;
  RelationName: string;
  RelationAddress?: string;
  RelationCity?: string;
  ActivityName: string;
  Text: string;
  StartDate: Date;
  EndDate: Date;
  Latitude: number;
  Longitude: number;
  SchedulerColor: string;
  Completed: boolean;
}

interface WorkTaskMapProps {
  tasks: MapWorkTask[];
  onTaskClick?: (workTaskOid: string) => void;
  isFullWidth?: boolean;
  onToggleFullWidth?: () => void;
}

/** A group of tasks at the same lat/lng */
interface LocationGroup {
  key: string;
  latitude: number;
  longitude: number;
  tasks: MapWorkTask[];
}

function formatDate(date: Date): string {
  const d = date instanceof Date ? date : new Date(date);
  return d.toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/** Fits the map bounds so all markers are visible */
function FitBoundsHelper({ groups }: { groups: LocationGroup[] }) {
  const { map, isLoaded } = useMap();
  const prevBoundsKey = useRef<string>('');

  if (map && isLoaded && groups.length > 0) {
    const boundsKey = groups.map(g => `${g.longitude},${g.latitude}`).sort().join('|');
    if (boundsKey !== prevBoundsKey.current) {
      prevBoundsKey.current = boundsKey;

      const lngs = groups.map(g => g.longitude);
      const lats = groups.map(g => g.latitude);
      const sw: [number, number] = [Math.min(...lngs), Math.min(...lats)];
      const ne: [number, number] = [Math.max(...lngs), Math.max(...lats)];

      if (sw[0] === ne[0] && sw[1] === ne[1]) {
        map.flyTo({ center: sw, zoom: 14 });
      } else {
        map.fitBounds([sw, ne], { padding: 50, maxZoom: 14 });
      }
    }
  }

  return null;
}

/** Rounds a coordinate to ~11m precision for grouping nearby tasks */
function coordKey(lat: number, lng: number): string {
  return `${lat.toFixed(4)},${lng.toFixed(4)}`;
}

export const WorkTaskMap = React.memo(({ tasks, onTaskClick, isFullWidth, onToggleFullWidth }: WorkTaskMapProps) => {
  const mapRef = useRef<MapRef>(null);

  // Deduplicate by WorkTaskOid
  const allUniqueTasks = useMemo(() => {
    const seen = new Set<string>();
    return tasks.filter(t => {
      if (seen.has(t.WorkTaskOid)) return false;
      seen.add(t.WorkTaskOid);
      return true;
    });
  }, [tasks]);

  // Split into tasks with and without coordinates
  const tasksWithCoords = useMemo(() =>
    allUniqueTasks.filter(t => t.Latitude !== 0 || t.Longitude !== 0),
    [allUniqueTasks]
  );

  const tasksWithoutCoords = useMemo(() =>
    allUniqueTasks.filter(t => t.Latitude === 0 && t.Longitude === 0),
    [allUniqueTasks]
  );

  // Group tasks by location so multiple tasks at the same place are visible
  const locationGroups: LocationGroup[] = useMemo(() => {
    const groupMap = new Map<string, LocationGroup>();
    for (const task of tasksWithCoords) {
      const key = coordKey(task.Latitude, task.Longitude);
      if (!groupMap.has(key)) {
        groupMap.set(key, { key, latitude: task.Latitude, longitude: task.Longitude, tasks: [] });
      }
      groupMap.get(key)!.tasks.push(task);
    }
    return Array.from(groupMap.values());
  }, [tasksWithCoords]);

  return (
    <div className='work-task-map-container'>
      <div className='map-header'>
        <h3>Kaart</h3>
        <div className='map-header-actions'>
          <span className='marker-count'>{allUniqueTasks.length}</span>
          {onToggleFullWidth && (
            <button
              className='fullwidth-btn'
              onClick={onToggleFullWidth}
              title={isFullWidth ? 'Kaart verkleinen' : 'Kaart vergroten'}
            >
              <span className={`dx-icon-${isFullWidth ? 'chevronright' : 'chevronleft'}`} />
            </button>
          )}
        </div>
      </div>
      <div className='map-wrapper'>
        <MapView
          ref={mapRef}
          center={[5.2913, 52.1326]}
          zoom={7}
          className='map-view'
          theme='light'
        >
          <MapControls showZoom showLocate={false} position='top-right' />
          {locationGroups.length > 0 && <FitBoundsHelper groups={locationGroups} />}
          {locationGroups.map((group) => (
            <MapMarker
              key={group.key}
              longitude={group.longitude}
              latitude={group.latitude}
            >
              <MarkerContent>
                {group.tasks.length === 1 ? (
                  <div
                    className='task-marker-dot'
                    style={{ backgroundColor: group.tasks[0].SchedulerColor || '#1976d2' }}
                  />
                ) : (
                  <div className='task-marker-cluster'>
                    <span>{group.tasks.length}</span>
                  </div>
                )}
              </MarkerContent>
              <MarkerPopup closeButton>
                <div className='popup-content'>
                  {group.tasks.length === 1 ? (
                    <SingleTaskPopup task={group.tasks[0]} onTaskClick={onTaskClick} />
                  ) : (
                    <MultiTaskPopup tasks={group.tasks} onTaskClick={onTaskClick} />
                  )}
                </div>
              </MarkerPopup>
            </MapMarker>
          ))}
        </MapView>
      </div>
      {tasksWithoutCoords.length > 0 && (
        <div className='no-coords-list'>
          <div className='no-coords-header'>
            <span className='dx-icon-warning' style={{ fontSize: 14, color: '#e67e22' }} />
            <span>Zonder locatie ({tasksWithoutCoords.length})</span>
          </div>
          <div className='no-coords-items'>
            {tasksWithoutCoords.map(task => (
              <div
                key={task.WorkTaskOid}
                className='no-coords-item'
                onClick={() => onTaskClick?.(task.WorkTaskOid)}
              >
                <span
                  className='item-color-dot'
                  style={{ backgroundColor: task.SchedulerColor || '#1976d2' }}
                />
                <div className='item-info'>
                  <span className='item-relation'>{task.RelationName}</span>
                  <span className='item-activity'>{task.ActivityName}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
});

/** Popup content for a single task */
function SingleTaskPopup({ task, onTaskClick }: { task: MapWorkTask; onTaskClick?: (oid: string) => void }) {
  return (
    <>
      <div className='popup-header'>
        <span className='popup-color-dot' style={{ backgroundColor: task.SchedulerColor || '#1976d2' }} />
        <div>
          <div className='popup-relation'>{task.RelationName}</div>
          <div className='popup-activity'>{task.ActivityName}</div>
        </div>
      </div>
      {(task.RelationAddress || task.RelationCity) && (
        <div className='popup-address'>
          {task.RelationAddress}{task.RelationAddress && task.RelationCity ? ', ' : ''}{task.RelationCity}
        </div>
      )}
      <div className='popup-dates'>
        <span>{formatDate(task.StartDate)}</span>
        <span> — </span>
        <span>{formatDate(task.EndDate)}</span>
      </div>
      {task.Completed && <div className='popup-completed'>✓ Afgerond</div>}
      {onTaskClick && (
        <button className='popup-open-btn' onClick={() => onTaskClick(task.WorkTaskOid)}>
          Open opdracht
        </button>
      )}
    </>
  );
}

/** Popup content showing multiple tasks at the same location */
function MultiTaskPopup({ tasks, onTaskClick }: { tasks: MapWorkTask[]; onTaskClick?: (oid: string) => void }) {
  const first = tasks[0];
  return (
    <>
      <div className='popup-location-header'>
        <div className='popup-relation'>{first.RelationName}</div>
        {(first.RelationAddress || first.RelationCity) && (
          <div className='popup-address'>
            {first.RelationAddress}{first.RelationAddress && first.RelationCity ? ', ' : ''}{first.RelationCity}
          </div>
        )}
        <div className='popup-task-count'>{tasks.length} opdrachten op deze locatie</div>
      </div>
      <div className='popup-task-list'>
        {tasks.map(task => (
          <div
            key={task.WorkTaskOid}
            className='popup-task-item'
            onClick={() => onTaskClick?.(task.WorkTaskOid)}
          >
            <span className='popup-color-dot' style={{ backgroundColor: task.SchedulerColor || '#1976d2' }} />
            <div className='popup-task-item-info'>
              <div className='popup-task-item-activity'>{task.ActivityName}</div>
              <div className='popup-task-item-dates'>
                {formatDate(task.StartDate)} — {formatDate(task.EndDate)}
              </div>
            </div>
            {task.Completed && <span className='popup-task-item-check'>✓</span>}
          </div>
        ))}
      </div>
    </>
  );
}
